import React, { useCallback, useContext, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useUserHasGrpcCache } from 'src/hooks/useUserHasFeature';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import {
  getMemoryUsage,
  getSystemPerformanceIndicators,
} from 'src/utils/miscUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ClientLogClient,
  StopwatchLogEntry,
  StopwatchStopGap,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import {
  IStopwatchEntry,
  IStopwatchLoggingContext,
  StopwatchLoggingEventName,
} from './StopwatchLoggingContext.types';

export const StopwatchLoggingContext =
  React.createContext<IStopwatchLoggingContext>({
    startEvent: () => false,
    addStopGap: () => false,
  });

export const useStopwatchLoggingContext = () =>
  useContext(StopwatchLoggingContext);

export const StopwatchLoggingContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { mainRoute, activeAccountWebClientConfig } = useAppContext();

  const hasGrpcCache = useUserHasGrpcCache();

  const logClient = useMemo(
    () => new ClientLogClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  // Store multiple stopwatch entries using logKey as the key
  const [stopwatchEntries, setStopwatchEntries] = React.useState<
    // the key is the LogId (not LogKey)
    // as we should only have one active entry per Log kind (e.g. InventoryEventPage)
    Map<string, IStopwatchEntry>
  >(new Map());

  const logAndStopIfAllGapsArePresent = useCallback(
    (newStopwatchEntry: IStopwatchEntry) => {
      if (
        newStopwatchEntry.logWhenAllGapsPresent.some(
          (gap) => newStopwatchEntry?.stopGaps[gap] == null
        )
      ) {
        return false;
      }

      // If the entry exists and the end time is not set, we can stop and log it
      if (newStopwatchEntry && newStopwatchEntry.endTime == null) {
        newStopwatchEntry.endTime = new Date();

        const memoryUsage = getMemoryUsage();

        // eslint-disable-next-line no-restricted-syntax
        const logEntry: StopwatchLogEntry = {
          applicationSource: REACT_APP_NAME,
          logId: newStopwatchEntry.id,
          message: newStopwatchEntry.id + ' | ' + mainRoute,
          pageUrl: window.location.href,
          startTime: newStopwatchEntry.startTime.toISOString(),
          endTime: newStopwatchEntry.endTime?.toISOString(),
          totalDurationMs:
            newStopwatchEntry.endTime!.getTime() -
            newStopwatchEntry.startTime.getTime(),
          stopGaps: newStopwatchEntry.stopGaps,
          hasGrpcCacheFeature: hasGrpcCache ?? false,
          browserCap: getSystemPerformanceIndicators(memoryUsage),
          ...memoryUsage,
        };

        console.debug('Stopwatch logged', logEntry.totalDurationMs, logEntry);

        tryInvokeApi(
          async () => {
            logClient.logStopwatch(logEntry);
          },
          () => {
            console.warn('Failed to log error to server');
          }
        );

        return true;
      }

      return false;
    },
    [hasGrpcCache, logClient, mainRoute]
  );

  const addStopGap = useCallback(
    (
      logKey: string,
      id: string,
      logWhenAllGapsPresent: string[],
      eventName: string,
      entityType?: ActionOutboxEntityType,
      newStopwatchEntry?: IStopwatchEntry
    ) => {
      setStopwatchEntries((prev) => {
        const updated = new Map(prev);
        let entry = newStopwatchEntry || updated.get(id);

        if (entry?.endTime != null) {
          // already ended, do not add more gaps
          return updated;
        }

        const memoryUsage = getMemoryUsage();

        if (
          // No entry yet, start a new one
          !entry ||
          // Logkey mismatch, start a new one
          logKey !== entry.key ||
          // Entity type mismatch, start a new one
          (entityType && entityType !== entry.entityType)
        ) {
          // Start a new entry (since sometimes event can come in before the start)
          entry = {
            id,
            key: logKey,
            entityType,
            startTime: new Date(),
            endTime: undefined,
            totalTimeMs: undefined,
            logWhenAllGapsPresent,
            stopGaps: {
              [StopwatchLoggingEventName.LogStart]: {
                time: new Date().toISOString(),
                name: StopwatchLoggingEventName.LogStart,
                logKey: logKey,
                browserCap: getSystemPerformanceIndicators(memoryUsage),
                ...memoryUsage,
              },
            },
          };
        }

        // only add gap if the event name doesn't exist yet
        const existingGap = entry.stopGaps[eventName];
        if (!existingGap) {
          const newGap: StopwatchStopGap = {
            time: new Date().toISOString(),
            name: eventName,
            logKey: logKey,
            browserCap: getSystemPerformanceIndicators(memoryUsage),
            ...memoryUsage,
          };

          const newEntry = {
            ...entry,
            stopGaps: {
              ...(eventName === StopwatchLoggingEventName.LogStart
                ? {} // if the event is PageStartLoad, clear existing gaps
                : entry.stopGaps),
              [eventName]: newGap,
            },
          };

          console.debug('Stopwatch gap', eventName, newEntry);

          logAndStopIfAllGapsArePresent(newEntry);

          updated.set(id, newEntry);
        }

        return updated;
      });

      return true;
    },
    [logAndStopIfAllGapsArePresent]
  );

  const startEvent = useCallback(
    (
      logKey: string,
      id: string,
      logWhenAllGapsPresent: string[],
      entityType?: ActionOutboxEntityType
    ) => {
      const existingEntry = stopwatchEntries.get(id);
      if (
        existingEntry?.key === logKey &&
        isEqual(existingEntry?.logWhenAllGapsPresent, logWhenAllGapsPresent)
      ) {
        stopwatchEntries.delete(id);
      }

      const newStopwatchEntry = {
        id,
        key: logKey,
        entityType,
        startTime: new Date(),
        endTime: undefined,
        totalTimeMs: undefined,
        stopGaps: {},
        logWhenAllGapsPresent,
      };

      console.debug(
        'Stopwatch started',
        newStopwatchEntry.startTime.toISOString(),
        newStopwatchEntry
      );

      addStopGap(
        logKey,
        id,
        logWhenAllGapsPresent,
        StopwatchLoggingEventName.LogStart,
        entityType,
        newStopwatchEntry
      );
    },
    [addStopGap, stopwatchEntries]
  );

  const contextValue = useMemo(
    () => ({
      startEvent,
      addStopGap,
    }),
    [addStopGap, startEvent]
  );

  return (
    <StopwatchLoggingContext.Provider value={contextValue}>
      {children}
    </StopwatchLoggingContext.Provider>
  );
};
